/**
 * `DetailHeader` - the member header card.
 *
 * Per the design handoff "Member 360" layout: a white card carrying the
 * record title, its status pills and a monospace meta line.
 *
 * The breadcrumb is NOT here. There is exactly one breadcrumb in the app
 * and it lives in the header bar, so this component publishes its trailing
 * crumb upward via `useSetBreadcrumbTrail` instead of rendering a second
 * trail beside it.
 *
 * Title. The record is an account, so it is called one - the module is
 * "Members" but the thing on screen is a single account belonging to a
 * member, and calling it a member would be a lie the moment a member holds
 * two accounts. It is titled by `ext_account_id`, the number a caller
 * reads out, rather than by the SM2 UUID: the UUID is a UUIDv7 whose
 * shortened form is a near-identical tail across records. The full id stays
 * in the meta line and in the Identifiers card, so nothing is lost.
 *
 * Deviation from the handoff, deliberately: the handoff's header card leads
 * with a 48px avatar monogram built from the member's initials. An SM2
 * account record has no name on it - the holder's name sits behind the
 * audited reveal in `HolderSection`. Deriving a monogram would paint a
 * fragment of PII into permanent chrome, outside the reveal-and-audit
 * control, so the avatar is omitted rather than faked.
 */
import { StatusPill } from '../../../components/status-pill'
import { useSetBreadcrumbTrail } from '../../../components/breadcrumb'
import type { AccountStatus } from '../../../lib/bff-client'
import { Timestamp } from './section'

export interface DetailHeaderProps {
  readonly accountId: string
  readonly extAccountId: string
  readonly provider: string
  readonly status: AccountStatus
  readonly appliedAt: string | null
}

export function DetailHeader({
  accountId,
  extAccountId,
  provider,
  status,
  appliedAt,
}: DetailHeaderProps): JSX.Element {
  useSetBreadcrumbTrail(extAccountId)

  return (
    <header className="flex flex-wrap items-center justify-between gap-murmuration-4 rounded-md border border-border-default bg-bg-surface p-murmuration-5 shadow-elev-1">
      <div className="flex flex-col gap-murmuration-1">
        <div className="flex flex-wrap items-center gap-murmuration-3">
          <h1
            id="account-detail-heading"
            tabIndex={-1}
            className="text-murmuration-h2 text-text-primary"
            aria-label={`Account ${extAccountId}, provider ${provider}, status ${status}`}
          >
            Account <span className="font-mono">{extAccountId}</span>
          </h1>
          <StatusPill status={status} />
          <span className="inline-flex items-center rounded-sm bg-brand-soft px-murmuration-2 py-[2px] text-murmuration-label font-medium text-brand-deep">
            {provider}
          </span>
        </div>
        <span className="font-mono text-murmuration-code text-text-muted">
          {accountId}
          {appliedAt !== null && appliedAt !== '' && (
            <>
              {' '}
              &middot; updated <Timestamp value={appliedAt} />
            </>
          )}
        </span>
      </div>
    </header>
  )
}
